
import { supabase } from '../client';
import { getCurrentUserOrganizationId } from '../products/organization';
import { Shop } from './types';
import { generateUniqueSlug, checkSlugExists } from './slugUtils';
import { createDefaultShopForm } from '../forms';

export async function createShop(shopData: Partial<Shop>): Promise<{ success: boolean; data?: Shop; error?: any }> {
  try {
    if (!shopData.organization_id) {
      const storedOrgId = localStorage.getItem('currentOrganizationId');
      shopData.organization_id = storedOrgId || await getCurrentUserOrganizationId();
      
      if (!shopData.organization_id) {
        console.error('[createShop] No organization ID found');
        return { success: false, error: 'No organization ID found' };
      }
    }
    
    if (shopData.name) {
      if (!shopData.slug) {
        shopData.slug = await generateUniqueSlug(shopData.name);
        console.log(`[createShop] Slug unique généré: ${shopData.slug}`);
      } else {
        const slugExists = await checkSlugExists(shopData.slug);
        if (slugExists) {
          shopData.slug = await generateUniqueSlug(shopData.name);
          console.log(`[createShop] Slug fourni existe déjà, nouveau slug généré: ${shopData.slug}`);
        }
      }
    }
    
    console.log(`[createShop] Creating shop for organization: ${shopData.organization_id}`, shopData);
    
    // Utiliser une transaction RPC pour créer la boutique et les paramètres de paiement en une seule opération
    const { data: shopWithSettings, error: rpcError } = await supabase.rpc(
      'create_shop_with_payment_settings',
      { 
        shop_data: shopData
      }
    );
    
    if (!rpcError && shopWithSettings) {
      console.log(`[createShop] Shop created successfully via RPC:`, shopWithSettings);
      
      try {
        // Créer le formulaire par défaut pour la boutique
        await createDefaultShopForm(
          shopWithSettings.id, 
          shopWithSettings.organization_id
        );
        console.log(`[createShop] Default form created for shop: ${shopWithSettings.id}`);
      } catch (formError) {
        console.error('[createShop] Error creating default form:', formError);
        // We continue even if form creation fails
      }
      
      return { success: true, data: shopWithSettings };
    }
    
    // Si la RPC échoue, essayer la méthode manuelle
    console.log(`[createShop] RPC failed, falling back to manual creation:`, rpcError);
    
    // Méthode manuelle de création
    const { data, error } = await supabase
      .from('shops')
      .insert([shopData])
      .select()
      .single();

    if (error) {
      console.error('[createShop] Error creating shop:', error);
      return { success: false, error };
    }

    console.log(`[createShop] Shop created successfully:`, data);
    
    if (data) {
      // Vérifier si des paramètres de paiement existent déjà pour cette boutique
      const { data: existingSettings } = await supabase
        .from('ecom_payment_settings')
        .select('id')
        .eq('shop_id', data.id)
        .limit(1);
        
      // Créer les paramètres de paiement seulement s'ils n'existent pas déjà
      if (!existingSettings || existingSettings.length === 0) {
        console.log(`[createShop] No payment settings found, creating default settings for shop: ${data.id}`);
        const paymentSettings = {
          shop_id: data.id,
          cod_enabled: true,
          stripe_enabled: false,
          paypal_enabled: false
        };
        
        const { error: settingsError } = await supabase
          .from('ecom_payment_settings')
          .insert([paymentSettings]);
          
        if (settingsError) {
          console.error('[createShop] Error creating payment settings:', settingsError);
          // Continue even if payment settings creation fails
        } else {
          console.log(`[createShop] Default payment settings created for shop: ${data.id}`);
        }
      } else {
        console.log(`[createShop] Payment settings already exist for shop: ${data.id}`);
      }
      
      try {
        // Créer le formulaire par défaut pour la boutique
        await createDefaultShopForm(
          data.id, 
          data.organization_id
        );
        console.log(`[createShop] Default form created for shop: ${data.id}`);
      } catch (formError) {
        console.error('[createShop] Error creating default form:', formError);
        // We continue even if form creation fails
      }
      
      return { success: true, data };
    }
    
    return { success: false, error: 'Unknown error occurred' };
  } catch (error) {
    console.error('[createShop] Exception in createShop:', error);
    return { success: false, error };
  }
}
